import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import { NavItem } from '../NavItem/NavItem';
import type { NavItemProps } from '../NavItem/NavItem';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { SidebarUserMenu } from './SidebarUserMenu';

/** Status dot for an AI Team child row. */
export type SidebarAgentStatus = 'on' | 'off' | 'paused' | 'locked';

export interface SidebarSubItem {
  key: string;
  label: string;
  status: SidebarAgentStatus;
}

export interface SidebarNav {
  icon: NavItemProps['icon'];
  label: string;
  /** Group id — a divider is drawn whenever this changes between rows. */
  group?: string;
  /** Numeric count. Zero renders nothing; capped at 99+. */
  count?: number;
  /** `alert` = red (a person must act) · `muted` = grey (information). */
  countTone?: NavItemProps['countTone'];
  /** Not-purchased: muted row with a padlock. */
  locked?: boolean;
  /** When present, this row becomes the (single) expandable row with a sublist. */
  children?: SidebarSubItem[];
  badge?: number;
  badgeText?: string;
  sparkle?: boolean;
  /** Pulsing live dot when inactive (e.g. Live feed). */
  live?: boolean;
  /** Amber on-hold dot (spec 36 §7): Messages / Email when that agent is held. */
  heldDot?: boolean;
  key: string;
}

export interface SidebarBrand {
  logo?: React.ReactNode;
  name: string;
  badge?: string;
}

export interface SidebarWorkspace {
  label: string;
  sublabel?: string;
  initials?: string;
  onClick?: () => void;
}

export interface SidebarStatusCard {
  title: string;
  subtitle: string;
}

export interface SidebarUser {
  name: string;
  role?: string;
  avatar?: string;
  initials?: string;
  onProfile?: () => void;
  onSettings?: () => void;
  onLogout?: () => void;
  showThemePicker?: boolean;
}

export interface SidebarProps {
  logo?: React.ReactNode;
  workspaceName?: string;
  brand?: SidebarBrand;
  workspace?: SidebarWorkspace;
  /** When set, replaces the default workspace button (e.g. location switcher). */
  workspaceSlot?: React.ReactNode;
  /** Optional product picker rendered below brand and above navigation. */
  productSlot?: React.ReactNode;
  nav: SidebarNav[];
  navSectionLabel?: string;
  active?: string;
  onNav?: (key: string) => void;
  /** Active AI Team child key (e.g. 'receptionist'), when on an agent page. */
  activeChild?: string;
  /** Navigate to an AI Team child. */
  onNavChild?: (key: string) => void;
  statusCard?: SidebarStatusCard;
  user?: SidebarUser;
  live?: boolean;
  userName?: string;
  userRole?: string;
  userAvatar?: string;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  className?: string;
}

const eyebrowClass =
  'font-[var(--font-mono)] text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--sidebar-muted)]';

const DOT_COLOR: Record<Exclude<SidebarAgentStatus, 'locked'>, string> = {
  on: 'var(--success)',
  off: 'var(--sidebar-muted)',
  paused: 'var(--gold)',
};

function AgentDot({ status, active }: { status: SidebarAgentStatus; active: boolean }) {
  if (status === 'locked') {
    return <Icon kind="lock" size={12} stroke={2} color={active ? 'var(--color-sidebar-active-foreground)' : 'var(--sidebar-muted)'} />;
  }
  return (
    <span
      aria-hidden
      className="h-[7px] w-[7px] shrink-0 rounded-full"
      style={{ background: active ? 'var(--color-sidebar-active-foreground)' : DOT_COLOR[status] }}
    />
  );
}

function AgentChildRow({
  item,
  active,
  onClick,
}: {
  item: SidebarSubItem;
  active: boolean;
  onClick: () => void;
}) {
  const label = `${item.label}, ${item.status}`;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex h-9 w-full items-center justify-between rounded-[10px] border border-transparent px-3 text-left text-[13.5px] outline-none transition-[background,box-shadow] duration-[160ms] ease-[var(--ease-out)]',
        active
          ? 'font-semibold text-[var(--color-sidebar-active-foreground)]'
          : 'font-medium text-[var(--sidebar-ink)] hover:bg-[var(--nav-hover-bg)]',
      )}
      style={
        active
          ? {
              background: 'var(--nav-active-bg)',
              borderColor: 'var(--nav-active-border)',
              boxShadow: 'var(--nav-active-shadow)',
            }
          : undefined
      }
    >
      <span className="truncate">{item.label}</span>
      <AgentDot status={item.status} active={active} />
    </button>
  );
}

interface ExpandableNavGroupProps {
  item: SidebarNav;
  active: boolean;
  activeChild: string | undefined;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  onNav: () => void;
  onNavChild: (key: string) => void;
  onExpandRail: () => void;
}

function ExpandableNavGroup({
  item,
  active,
  activeChild,
  collapsed,
  open,
  onToggle,
  onNav,
  onNavChild,
  onExpandRail,
}: ExpandableNavGroupProps) {
  const [hovered, setHovered] = useState(false);
  const children = item.children ?? [];

  // Three parent states: on roster (filled pill) · in section on a child (accent, no fill) · default.
  const onRoster = active && !activeChild;
  const inSectionChild = active && activeChild !== undefined;
  const hasCount = item.count !== undefined && item.count > 0;
  const accent = onRoster ? 'var(--color-sidebar-active-foreground)' : inSectionChild ? 'var(--ai-3)' : 'var(--sidebar-icon)';

  if (collapsed) {
    return (
      <NavItem icon={item.icon} label={item.label} active={active} compact onClick={onExpandRail} />
    );
  }

  return (
    <div>
      <div className="relative">
        <button
          type="button"
          onClick={onNav}
          onMouseEnter={() => {
            setHovered(true);
          }}
          onMouseLeave={() => {
            setHovered(false);
          }}
          className={cn(
            'relative grid w-full grid-cols-[18px_1fr_auto] items-center gap-3 rounded-[12px] py-2 pl-[11px] pr-[36px] text-left outline-none transition-[background,box-shadow,border-color] duration-[160ms] ease-[var(--ease-out)]',
            onRoster
              ? 'font-semibold text-[var(--color-sidebar-active-foreground)]'
              : inSectionChild
                ? 'font-semibold text-[var(--ai-3)]'
                : 'font-medium text-[var(--sidebar-ink)]',
            'text-[13.5px]',
          )}
          style={
            onRoster
              ? {
                  background: 'var(--nav-active-bg)',
                  border: '1px solid var(--nav-active-border)',
                  boxShadow: 'var(--nav-active-shadow)',
                }
              : hovered
                ? { background: 'var(--nav-hover-bg)', border: '1px solid var(--nav-hover-border)' }
                : { background: 'transparent', border: '1px solid transparent' }
          }
        >
          {/* Design system rev 1: flat active pill — no gloss overlay. */}
          <Icon kind={item.icon} size={16} stroke={1.7} color={accent} />
          <span>{item.label}</span>
          {hasCount ? (
            <span
              className={cn(
                'inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-[16px]',
                onRoster ? 'bg-white/25 text-white' : 'bg-[var(--ws-card-bg)] text-[var(--sidebar-muted)]',
              )}
            >
              {item.count}
            </span>
          ) : (
            <span />
          )}
        </button>
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? 'Hide the agent list' : 'Show the agent list'}
          onClick={onToggle}
          className="absolute right-1.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-[8px] outline-none hover:bg-[var(--nav-hover-bg)]"
        >
          <Icon kind={open ? 'down' : 'right'} size={14} stroke={2} color={accent} />
        </button>
      </div>

      {open && (
        <div className="relative mb-1 mt-0.5 pl-[26px]">
          <span
            aria-hidden
            className="absolute bottom-1 left-[17px] top-1 w-px bg-[var(--sidebar-border)]"
          />
          <div className="flex flex-col gap-0.5">
            {children.map((c) => (
              <AgentChildRow
                key={c.key}
                item={c}
                active={activeChild === c.key}
                onClick={() => {
                  onNavChild(c.key);
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Sidebar({
  logo,
  workspaceName,
  brand,
  workspace,
  workspaceSlot,
  productSlot,
  nav,
  navSectionLabel = 'Navigate',
  active,
  onNav,
  activeChild,
  onNavChild,
  statusCard,
  user,
  live,
  userName,
  userRole,
  collapsed = false,
  onCollapsedChange,
  className,
}: SidebarProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Manual chevron-open for the expandable AI Team row; resets on any navigation.
  const [manualOpen, setManualOpen] = useState(false);
  useEffect(() => {
    setManualOpen(false);
  }, [active]);

  const resolvedBrand: SidebarBrand = brand ?? {
    logo,
    name: workspaceName ?? 'Workspace',
  };

  const resolvedUser: SidebarUser | undefined =
    user ??
    (userName
      ? {
          name: userName,
          ...(userRole !== undefined ? { role: userRole } : {}),
        }
      : undefined);

  const resolvedStatus = statusCard ?? (live ? { title: 'Live', subtitle: '' } : undefined);

  return (
    <div
      className={cn(
        'fd-sidebar-frame sticky top-2 z-[var(--z-sidebar)] my-2 ml-2 h-[calc(100vh-16px)] shrink-0 rounded-[22px_24px_24px_22px]',
        'transition-[width] duration-300 ease-[var(--ease-out)]',
        collapsed ? 'w-[var(--sidebar-w-collapsed)]' : 'w-[var(--sidebar-w)]',
        className,
      )}
    >
    <aside
      className={cn(
        'fd-sidebar flex h-full w-full flex-col overflow-hidden rounded-[22px_24px_24px_22px] py-4',
        collapsed ? 'px-2' : 'px-[13px]',
      )}
      style={{
        background: 'var(--sidebar-bg)',
        backdropFilter: 'var(--sidebar-backdrop)',
        WebkitBackdropFilter: 'var(--sidebar-backdrop)',
        border: '1px solid var(--sidebar-border)',
        boxShadow: 'var(--sidebar-shadow)',
      }}
    >
      <div
        className={cn(
          'mb-[22px] flex shrink-0 items-center gap-2',
          collapsed ? 'flex-col gap-2.5 px-0' : 'justify-between px-1.5 pt-0.5'
        )}
      >
        <div className={cn('flex min-w-0 items-center gap-2.5', collapsed && 'justify-center')}>
          {resolvedBrand.logo ?? (
            <div className="h-[26px] w-[26px] shrink-0 rounded-lg bg-[var(--grad-spectrum)]" />
          )}
          {!collapsed && (
            <>
              <span className="truncate font-[var(--font-display)] text-lg font-semibold tracking-[-0.02em] text-[var(--sidebar-ink-strong)]">
                {resolvedBrand.name}
              </span>
              {resolvedBrand.badge && (
                <span className="ml-auto shrink-0 rounded-[5px] bg-[var(--ai-tint)] px-[7px] py-0.5 font-[var(--font-mono)] text-[9.5px] font-semibold text-[var(--ai-3)]">
                  {resolvedBrand.badge}
                </span>
              )}
            </>
          )}
        </div>
        {onCollapsedChange && (
          <IconButton
            icon={collapsed ? 'right' : 'left'}
            size={30}
            label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => {
              onCollapsedChange(!collapsed);
              setUserMenuOpen(false);
            }}
          />
        )}
      </div>

      {productSlot ? (
        <div className={cn('mb-[18px] shrink-0', collapsed && 'flex justify-center')}>{productSlot}</div>
      ) : null}

      <div className="fd-nav-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        {!collapsed && <div className={cn(eyebrowClass, 'px-2 pb-2')}>{navSectionLabel}</div>}
        <div className="flex flex-col gap-0.5">
          {nav.map((item, i) => {
            const prev = i > 0 ? nav[i - 1] : undefined;
            const showDivider =
              prev !== undefined && item.group !== undefined && item.group !== prev.group;
            return (
              <React.Fragment key={item.key}>
                {showDivider && (
                  <div
                    role="separator"
                    aria-hidden
                    className={cn(
                      'my-[7px] h-px bg-[var(--sidebar-border)]',
                      collapsed ? 'mx-3' : 'mx-6',
                    )}
                  />
                )}
                {item.children && item.children.length > 0 ? (
                  <ExpandableNavGroup
                    item={item}
                    active={active === item.key}
                    activeChild={activeChild}
                    collapsed={collapsed}
                    open={active === item.key || manualOpen}
                    onToggle={() => {
                      setManualOpen((v) => !v);
                    }}
                    onNav={() => {
                      onNav?.(item.key);
                    }}
                    onNavChild={(k) => {
                      onNavChild?.(k);
                    }}
                    onExpandRail={() => {
                      onCollapsedChange?.(false);
                      setManualOpen(true);
                    }}
                  />
                ) : (
                  <NavItem
                    icon={item.icon}
                    label={item.label}
                    active={active === item.key}
                    compact={collapsed}
                    onClick={() => {
                      onNav?.(item.key);
                    }}
                    {...(item.count !== undefined ? { count: item.count } : {})}
                    {...(item.countTone !== undefined ? { countTone: item.countTone } : {})}
                    {...(item.locked !== undefined ? { locked: item.locked } : {})}
                    {...(item.badge !== undefined ? { badge: item.badge } : {})}
                    {...(item.badgeText !== undefined ? { badgeText: item.badgeText } : {})}
                    {...(item.sparkle !== undefined ? { sparkle: item.sparkle } : {})}
                    {...(item.live !== undefined ? { live: item.live } : {})}
                    {...(item.heldDot !== undefined ? { heldDot: item.heldDot } : {})}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="mt-auto flex shrink-0 flex-col gap-2.5 pt-[22px]">
        {resolvedStatus && !collapsed && (
          <div
            className="flex items-center gap-2.5 rounded-[13px] px-[13px] py-[11px]"
            style={{
              background: 'var(--ws-card-bg)',
              border: '1px solid var(--ws-card-border)',
              boxShadow: 'var(--ws-card-shadow)',
              backdropFilter: 'var(--ws-card-backdrop)',
              WebkitBackdropFilter: 'var(--ws-card-backdrop)',
            }}
          >
            <span className="cal-live h-[7px] w-[7px]" />
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] font-semibold text-[var(--sidebar-ink-strong)]">
                {resolvedStatus.title}
              </span>
              {resolvedStatus.subtitle && (
                <span className="mt-px block font-[var(--font-mono)] text-[10.5px] text-[var(--sidebar-muted)]">
                  {resolvedStatus.subtitle}
                </span>
              )}
            </span>
          </div>
        )}

        {resolvedStatus && collapsed && (
          <div
            className="mx-auto h-[7px] w-[7px] rounded-full bg-[var(--ai-3)]"
            title={resolvedStatus.title}
          />
        )}
        {workspaceSlot ? (
          <div className={cn('shrink-0', collapsed && 'flex justify-center')}>{workspaceSlot}</div>
        ) : workspace && !collapsed ? (
          <div>
            <div className={cn(eyebrowClass, 'px-2 pb-2')}>Workspace</div>
            <button
              type="button"
              onClick={workspace.onClick}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-[13px] px-[11px] py-[9px] text-left font-[inherit]',
                workspace.onClick ? 'cursor-pointer' : 'cursor-default'
              )}
              style={{
                background: 'var(--ws-card-bg)',
                border: '1px solid var(--ws-card-border)',
                boxShadow: 'var(--ws-card-shadow)',
                backdropFilter: 'var(--ws-card-backdrop)',
                WebkitBackdropFilter: 'var(--ws-card-backdrop)',
              }}
            >
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] bg-[var(--avatar-clay)] text-[11px] font-bold text-white">
                {workspace.initials ?? workspace.label.slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-[var(--sidebar-ink-strong)]">
                  {workspace.label}
                </span>
                {workspace.sublabel && (
                  <span className="mt-px block text-[11px] text-[var(--sidebar-muted)]">
                    {workspace.sublabel}
                  </span>
                )}
              </span>
              <Icon kind="down" size={14} color="var(--sidebar-icon)" />
            </button>
          </div>
        ) : null}
        {resolvedUser && (
          <SidebarUserMenu
            user={resolvedUser}
            open={userMenuOpen}
            onOpenChange={setUserMenuOpen}
            collapsed={collapsed}
          />
        )}
      </div>
    </aside>
    </div>
  );
}
